import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import { confirmDialog } from "../../../utils/confirm";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { getBasicQuizQuestions, saveBasicQuizQuestions } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import { ONLY_ALPHABETS } from "../../../config/regex";
import Swal from "sweetalert2";

const AdminCMSBasicQuiz = () => {
  const [items, setItems] = useState([]);
  const formRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    question: "",
    option0: "",
    option1: "",
    option2: "",
    option3: "",
    correct: 0,
  });
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);

  // Warn if user is mid-editing a question
  useUnsavedWarning(isAdding || !!editingId);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const stored = await getBasicQuizQuestions();
        if (!mounted) return;
        const list = Array.isArray(stored)
          ? stored.map((q) => ({ ...q, options: q.options || [] }))
          : [];
        setItems(list);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load questions",
        );
        setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setIsAdding(true);
    setForm({
      question: "",
      option0: "",
      option1: "",
      option2: "",
      option3: "",
      correct: 0,
    });
    setErrors({});
    setTouched({});
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    setIsAdding(false);
    const op = q.options || [];
    setForm({
      question: q.question || "",
      option0: op[0] || "",
      option1: op[1] || "",
      option2: op[2] || "",
      option3: op[3] || "",
      correct: typeof q.correct === "number" ? q.correct : 0,
    });
    setErrors({});
    setTouched({});
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    setForm({
      question: "",
      option0: "",
      option1: "",
      option2: "",
      option3: "",
      correct: 0,
    });
    setErrors({});
    setTouched({});
  };

  const validateField = (name, value) => {
    if (name === "correct") return "";
    const trimmed = String(value || "").trim();
    if (!trimmed) return "This field is required";
    return "";
  };

  const isFormInvalid = useMemo(() => {
    const fields = ["question", "option0", "option1", "option2", "option3"];
    for (const f of fields) {
      if (validateField(f, form[f])) return true;
    }
    return false;
  }, [form]);

  const isChanged = useMemo(() => {
    if (!editingId) return true;
    const original = items.find((q) => q.id === editingId);
    if (!original) return true;

    const op = original.options || [];
    return (
      form.question !== original.question ||
      form.option0 !== (op[0] || "") ||
      form.option1 !== (op[1] || "") ||
      form.option2 !== (op[2] || "") ||
      form.option3 !== (op[3] || "") ||
      form.correct !== original.correct
    );
  }, [form, editingId, items]);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  const saveItem = async () => {
    if (isFormInvalid) return;
    const options = [
      form.option0,
      form.option1,
      form.option2,
      form.option3,
    ].map((o) => (o || "").trim());
    const payload = {
      id: editingId || `bq-${Date.now()}`,
      question: form.question.trim(),
      options,
      correct: Number(form.correct),
    };
    let next;
    if (editingId) {
      next = items.map((q) => (q.id === editingId ? payload : q));
    } else {
      next = [...items, payload];
    }
    try {
      const saved = await saveBasicQuizQuestions(next);
      setItems(saved.length ? saved : next);
      setError("");
      cancelEdit();
      setIsAdding(false);
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: `Question ${editingId ? "updated" : "added"} successfully`,
        confirmButtonColor: "#38915c",
      });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          e.response?.data?.message || e.message || "Failed to save question",
        confirmButtonColor: "#38915c",
      });
    }
  };

  const removeItem = async (id) => {
    const confirmed = await confirmDialog({
      title: "Remove this question?",
      text: "It will be removed from the Basic Quiz (public page).",
    });
    if (!confirmed) return;
    const next = items.filter((q) => q.id !== id);
    try {
      const saved = await saveBasicQuizQuestions(next);
      setItems(saved.length ? saved : next);
      if (editingId === id) cancelEdit();
      setError("");
      Swal.fire({
        icon: "success",
        title: "Removed",
        text: "Question removed successfully",
        confirmButtonColor: "#38915c",
      });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          e.response?.data?.message || e.message || "Failed to remove question",
        confirmButtonColor: "#38915c",
      });
    }
  };

  const moveItem = async (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const next = [...items];
    [next[index], next[newIndex]] = [next[newIndex], next[index]];
    setItems(next);
    try {
      const saved = await saveBasicQuizQuestions(next);
      setItems(saved.length ? saved : next);
      setError("");
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          e.response?.data?.message ||
          e.message ||
          "Failed to reorder questions",
        confirmButtonColor: "#38915c",
      });
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/cms"
            className="text-sm text-primary-600 hover:underline mb-1 inline-block"
          >
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Basic Quiz</h1>
          <p className="text-gray-600 text-sm mt-1">
            Edit the 5 basic quiz questions shown on the public Basic Quiz page
            (/quiz). Visible to everyone without login.
          </p>
        </div>

        {!editingId && !isAdding && (
          <Button variant="primary" size="sm" onClick={openAdd}>
            <Plus className="w-4 h-4 mr-1 inline" />
            Add question
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      {(editingId || isAdding) && (
        <div
          ref={formRef}
          className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200"
        >
          <h2 className="font-semibold text-gray-800 mb-4">
            {editingId ? "Edit question" : "Add question"}
          </h2>
          <div className="space-y-4 mb-4">
            <TextInput
              label="Question"
              value={form.question}
              onChange={(e) => handleChange("question", e.target.value)}
              error={touched.question && errors.question}
              placeholder="e.g. What is the full form of NSE?"
              required
            />
            {[0, 1, 2, 3].map((i) => (
              <TextInput
                key={i}
                label={`Option ${i + 1}`}
                value={form[`option${i}`]}
                onChange={(e) => handleChange(`option${i}`, e.target.value)}
                error={touched[`option${i}`] && errors[`option${i}`]}
                placeholder={`Option ${i + 1}`}
                required
              />
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct answer (Option 1–4)
              </label>
              <select
                value={form.correct}
                onChange={(e) =>
                  setForm((f) => ({ ...f, correct: Number(e.target.value) }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {[0, 1, 2, 3].map((i) => (
                  <option key={i} value={i}>
                    Option {i + 1}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={saveItem}
              disabled={isFormInvalid || (editingId && !isChanged)}
            >
              {editingId ? "Update" : "Add"}
            </Button>
            <Button variant="secondary" size="sm" onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 w-12">
                #
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                Question
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700 w-24">
                Order
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700 w-28">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 text-center text-gray-500">
                  No questions yet. Add questions to show on the public Basic
                  Quiz page.
                </td>
              </tr>
            ) : (
              items.map((q, i) => (
                <tr
                  key={q.id}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-3 px-4 font-medium text-gray-600">
                    {i + 1}
                  </td>
                  <td className="py-3 px-4 text-gray-800">
                    {q.question || "—"}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => moveItem(i, "up")}
                      disabled={i === 0}
                      className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 mr-0.5"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(i, "down")}
                      disabled={i === items.length - 1}
                      className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                    >
                      ↓
                    </button>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(q)}
                      className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 inline-flex items-center justify-center"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(q.id)}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 inline-flex items-center justify-center ml-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCMSBasicQuiz;
